import { useNavigate } from "react-router-dom";
import Card from "../Card";
import { RecentTransactionListSkeleton } from "../../../components/Skeleton/TransactionHistorySkeleton";
import EmptyNewCryptoTransaction from "../../../components/Empty/EmptyNewCryptoTransaction";
import { FormatDate } from "../../../helper";

const STATUS_COLOR_MAP = {
  approved: "text-[#4ab932]", // Green
  pending: "text-amber-500",  // Yellow
  initiated: "text-blue-500", // Blue
  rejected: "text-red-500",   // Red
  cancelled: "text-red-500",   // Red
};

const TransactionRow = ({ tx, showBorder }) => {
  const nature = tx.transaction_nature?.toLowerCase() || "";
  const type = tx.transaction_type?.toLowerCase() || "";

  const isCredit = type === "credit";

  // Display title fallback
  const displayTitle = tx.remarks && tx.remarks.trim().length > 0
      ? tx.remarks
      : tx.account_title || `Transaction #${tx.transaction_id}`;

  // Primary amount (main balance change for this account entry)
  const primarySign = isCredit ? "+" : "-";
  const primaryColorClass = isCredit ? "text-[#4ab932]" : "text-error-500";
  const primaryAmountText = `${primarySign} ${tx.amount} ${tx.crypto_currency_code || ""}`;

  // Secondary counter-currency amount calculation
  const getSecondaryAmountText = () => {
    if (!tx.crypto_amount || !tx.currency_code) {
      return null;
    }

    let secondarySign = "";

    if (nature === "buy" || nature === "sell" || nature === "swap") {
      // Counter currency takes the opposite directional sign of the primary flow
      secondarySign = isCredit ? "-" : "+";
    } else {
      secondarySign = isCredit ? "-" : "+";
    }

    return `${secondarySign} ${tx.crypto_amount} ${tx.currency_code}`;
  };

  const secondaryText = getSecondaryAmountText();

  const normalizedStatus = tx.status?.toLowerCase() || "";
  const statusColorClass = STATUS_COLOR_MAP[normalizedStatus] || "text-text-secondary";

  return (
    <li className={`px-3.5 py-[15px] ${showBorder ? "border-b border-divider" : ""}`}>
      <div className="flex items-center justify-between gap-4">
        {/* Left Section: Logo, Remarks & Date */}
        <div className="flex min-w-0 items-center gap-2.5">
          {/* Outer relative wrapper: allows the badge to peek outside without getting clipped */}
          <div className="relative size-9 shrink-0">
            {/* Inner circle with overflow-hidden for the main avatar */}
            <div
              className="size-full overflow-hidden rounded-full bg-background-muted flex items-center justify-center"
              style={tx.iconBg ? { backgroundColor: tx.iconBg } : undefined}
            >
              <img
                src={tx.logo}
                alt={tx.crypto_currency_code || "crypto logo"}
                className="size-full rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            {/* Network/Asset badge placed outside the overflow-hidden container */}
            {tx?.asset && (
              <img
                src={tx?.asset?.logo_url}
                alt={tx?.asset?.name}
                className="absolute -bottom-1 -right-0 h-4 w-4 rounded-full border border-background bg-background object-contain shadow-sm"
              />
            )}
          </div>

          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-[19.5px] text-text-primary">
              {displayTitle}
            </p>

            <div className="flex items-center gap-1 md:gap-1.5 text-xs text-text-secondary">
              <span>{FormatDate(tx.transaction_date, "d m, yy")}</span>
              {tx.transaction_nature && (
                <>
                  <span>•</span>
                  <span className="capitalize">{tx.transaction_nature}</span>
                </>
              )}
              {tx.status && (
                <>
                  <span>•</span>
                  <span className={`capitalize ${statusColorClass}`}>
                    {tx.status}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Section: Primary & Secondary Amounts */}
        <div className="flex shrink-0 flex-col items-end gap-px">
          <p className={`whitespace-nowrap text-sm font-semibold leading-[19.5px] ${primaryColorClass}`}>
            {primaryAmountText}
          </p>

          {secondaryText && (
            <p
              className={`whitespace-nowrap text-xs leading-[16.5px] text-text-secondary ${
                tx.secondaryMedium ? "font-medium" : "font-normal"
              }`}
            >
              {secondaryText}
            </p>
          )}
        </div>
      </div>
    </li>
  );
};


const RecentTransactions = ({
  className = "",
  isLoading,
  recentTransactions,
  recentTrnLimit = 6,
}) => {
  const navigate = useNavigate();

  return (
    <Card className={`flex h-full flex-col overflow-hidden  ${className}`}>
      <div className="flex h-[54px] shrink-0 items-center justify-between border-b border-divider px-4 pb-[17px] pt-4">
        <h2 className="text-base font-semibold leading-[21px] text-text-primary">
          Recent transactions
        </h2>

        <button
          type="button"
          onClick={() => navigate("/v2/crypto-transaction-history")}
          className="text-xs font-semibold leading-[18px] text-text-secondary underline custom-transition hover:text-text-primary"
        >
          See All Transactions
        </button>
      </div>
      <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-4">
        {isLoading ? (
          <RecentTransactionListSkeleton recentTrnLimit={recentTrnLimit} />
        ) : (
          <>
            {recentTransactions && recentTransactions.length === 0 ? (
              <EmptyNewCryptoTransaction />
            ) : (
              recentTransactions.map((tx, index) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  showBorder={index < recentTransactions.length - 1}
                />
              ))
            )}
          </>
        )}
      </ul>
    </Card>
  );
};

export default RecentTransactions;
